const ENCRYPTION_VERSION = 1;
const PBKDF2_ITERATIONS = 100000;
const SALT_BYTES = 16;
const IV_BYTES = 12;
let userKey = null;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
function bytesToBase64(bytes) {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
}
function base64ToBytes(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}
function toArrayBuffer(bytes) {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}
function isEncryptedPayload(value) {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const payload = value;
    return (payload.v === ENCRYPTION_VERSION &&
        typeof payload.iv === 'string' &&
        typeof payload.salt === 'string' &&
        typeof payload.data === 'string');
}
async function deriveKey(salt) {
    if (!userKey) {
        throw new Error('Encryption key not set');
    }
    const keyMaterial = await crypto.subtle.importKey('raw', textEncoder.encode(userKey), 'PBKDF2', false, ['deriveKey']);
    return crypto.subtle.deriveKey({
        name: 'PBKDF2',
        salt: toArrayBuffer(salt),
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256'
    }, keyMaterial, {
        name: 'AES-GCM',
        length: 256
    }, false, ['encrypt', 'decrypt']);
}
async function encryptData(data) {
    const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
    const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const key = await deriveKey(salt);
    const encoded = textEncoder.encode(JSON.stringify(data));
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: toArrayBuffer(iv) }, key, toArrayBuffer(encoded));
    return {
        v: ENCRYPTION_VERSION,
        iv: bytesToBase64(iv),
        salt: bytesToBase64(salt),
        data: bytesToBase64(new Uint8Array(encrypted))
    };
}
async function decryptData(payload) {
    const salt = base64ToBytes(payload.salt);
    const iv = base64ToBytes(payload.iv);
    const data = base64ToBytes(payload.data);
    const key = await deriveKey(salt);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: toArrayBuffer(iv) }, key, toArrayBuffer(data));
    const decoded = textDecoder.decode(decrypted);
    return JSON.parse(decoded);
}
async function save(key, data, options = {}) {
    if (options.encrypted) {
        if (!userKey) {
            console.warn(`Missing encryption key for ${key}`);
            return;
        }
        const payload = await encryptData(data);
        localStorage.setItem(key, JSON.stringify(payload));
        return;
    }
    localStorage.setItem(key, JSON.stringify(data));
}
async function load(key, options = {}) {
    const item = localStorage.getItem(key);
    if (!item) {
        return null;
    }
    try {
        const parsed = JSON.parse(item);
        if (options.encrypted) {
            if (!userKey) {
                return null;
            }
            if (!isEncryptedPayload(parsed)) {
                return null;
            }
            return (await decryptData(parsed));
        }
        return parsed;
    }
    catch (error) {
        console.error('Error parsing storage item', error);
        return null;
    }
}
export const storage = {
    save,
    load,
    remove: (key) => {
        localStorage.removeItem(key);
    },
    setUserKey: (key) => {
        userKey = key;
    },
    clearUserKey: () => {
        userKey = null;
    },
    hasKey: () => userKey !== null
};
